import { css, ClassNames } from '@emotion/react';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { colors, darkmodeStyles, fontStyles, spacing, buildPageSpacing } from '../../styles';
import { General } from '../../stores';
import briefcaseIconInactive from './briefcaseInactive.svg';
import briefcaseIconActive from './briefcaseActive.svg';
import gearActive from './gearActive.svg';
import gearInactive from './gearInactive.svg';
import chatIcon from './chatInactive.svg';
import chatIconActive from './chatActive.svg';
import magnifyingGlassActive from './magnifyingGlassActive.svg';
import magnifyingGlassInactive from './magnifyingGlassInactive.svg';
import lineChartActive from './lineChartActive.svg';
import lineChartInactive from './lineChartInactive.svg';

const NavigationBar = () => {
  const { t } = useTranslation();
  const isKeyboardVisible = General.useState((s) => s.isKeyboardVisible);
  const history = useHistory();
  const isAndroid = Capacitor.getPlatform() === 'android';

  const navigationBarStyles = css`
    z-index: 101;
    background-color: ${colors.sugar};
    position: fixed;
    width: 100%;
    left: 0;
    bottom: 0;
    height: ${isAndroid ? buildPageSpacing('88px', 'BOTTOM') : buildPageSpacing(spacing.giga, 'BOTTOM')};
    padding-bottom: ${isAndroid ? buildPageSpacing('24px', 'BOTTOM') : buildPageSpacing('0px', 'BOTTOM')};
    display: ${isKeyboardVisible ? 'none' : 'flex'};
    border-top: 1px solid rgba(136, 133, 129, 0.25);
    ${darkmodeStyles.regular}

    /* CRITICAL FIX: Prevent first touch from being interpreted as scroll */
    pointer-events: auto;
    touch-action: none;
    -webkit-overflow-scrolling: auto;
    overscroll-behavior: contain;

    /* Fast transition for keyboard response */
    transition:
      opacity 0.1s ease-out,
      transform 0.1s ease-out;
    opacity: ${isKeyboardVisible ? 0 : 1};
    transform: ${isKeyboardVisible ? 'translateY(100%)' : 'translateY(0)'};
  `;

  // Check if current path is portfolios or a sub-route
  const isPortfoliosActive = history.location.pathname.includes('/portfolios');
  // Check if current path is markets or a sub-route
  const isMarketsActive = history.location.pathname.startsWith('/markets');
  // Check if current path is ideas or a sub-route
  const isIdeasActive = history.location.pathname.startsWith('/ideas');

  return (
    <div css={navigationBarStyles}>
      <ClassNames>
        {({ css }) => {
          const styles = {
            link: css`
              flex: 1;
              ${fontStyles.flea};
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              color: ${colors.silver};
              html[data-theme='dark'] & {
                background-color: ${colors.liquorice};
                color: rgba(255, 255, 255, 0.7);
              }

              /* Prevent touch from being interpreted as scroll */
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
              cursor: pointer;
            `,
            activeLink: css`
              color: ${colors.petrolBlue};
              html[data-theme='dark'] & {
                color: ${colors.petrolBlue};
              }
            `,
            icon: css`
              height: 22px;
              margin-bottom: ${spacing.milli};

              /* Fix: Make images properly receive touch events */
              pointer-events: none; /* Let clicks pass through to parent link */
              user-select: none;
              -webkit-user-select: none;
              -webkit-touch-callout: none;
            `,
            iconInactive: css`
              height: 22px;
              margin-bottom: ${spacing.milli};
              pointer-events: none;
              user-select: none;
              -webkit-user-select: none;
              -webkit-touch-callout: none;

              html[data-theme='dark'] & {
                filter: brightness(0) invert(1);
                opacity: 0.7;
              }
            `,
          };
          return (
            <>
              <NavLink className={styles.link} activeClassName={styles.activeLink} to="/portfolios">
                <img
                  src={isPortfoliosActive ? briefcaseIconActive : briefcaseIconInactive}
                  className={isPortfoliosActive ? styles.icon : styles.iconInactive}
                />
                <span>{t('nav.portfolio')}</span>
              </NavLink>
              <NavLink
                className={styles.link}
                activeClassName={styles.activeLink}
                to="/ideas"
                isActive={() => isIdeasActive}
              >
                <img
                  src={isIdeasActive ? magnifyingGlassActive : magnifyingGlassInactive}
                  className={isIdeasActive ? styles.icon : styles.iconInactive}
                />
                <span>{t('nav.ideas')}</span>
              </NavLink>
              <NavLink
                className={styles.link}
                activeClassName={styles.activeLink}
                to="/markets"
                isActive={() => isMarketsActive}
              >
                <img
                  src={isMarketsActive ? lineChartActive : lineChartInactive}
                  className={isMarketsActive ? styles.icon : styles.iconInactive}
                />
                <span>{t('nav.markets')}</span>
              </NavLink>
              <NavLink className={styles.link} activeClassName={styles.activeLink} exact to="/chat">
                <img
                  src={history.location.pathname === '/chat' ? chatIconActive : chatIcon}
                  className={history.location.pathname === '/chat' ? styles.icon : styles.iconInactive}
                />
                <span>{t('nav.chat')}</span>
              </NavLink>
              <NavLink className={styles.link} activeClassName={styles.activeLink} exact to="/more">
                <img
                  src={history.location.pathname === '/more' ? gearActive : gearInactive}
                  className={history.location.pathname === '/more' ? styles.icon : styles.iconInactive}
                />
                <span>{t('nav.settings')}</span>
              </NavLink>
            </>
          );
        }}
      </ClassNames>
    </div>
  );
};

export default NavigationBar;
